import type { JSX } from 'react';
import './error-banner.css';

export type ErrorBannerSeverity = 'error' | 'warning' | 'info';

export interface ErrorBannerAction {
  label: string;
  onClick: () => void;
}

export interface ErrorBannerProps {
  /** Warm, blame-free message. */
  message: string;
  /** Optional primary action (e.g. "Try again"). */
  action?: ErrorBannerAction;
  /** When provided, renders a dismiss button. */
  onDismiss?: () => void;
  /** Severity icon/color variant. Default: 'error'. */
  severity?: ErrorBannerSeverity;
  /** data-testid override. Default: 'error-banner'. */
  testId?: string;
}

const ICON_BY_SEVERITY: Record<ErrorBannerSeverity, string> = {
  error: '⚠',
  warning: '⚠',
  info: 'ℹ',
};

export function ErrorBanner({
  message,
  action,
  onDismiss,
  severity = 'error',
  testId = 'error-banner',
}: ErrorBannerProps): JSX.Element {
  return (
    <div
      role="alert"
      aria-live="assertive"
      className={`error-banner error-banner--${severity}`}
      data-testid={testId}
    >
      <span className="error-banner__icon" aria-hidden="true">
        {ICON_BY_SEVERITY[severity]}
      </span>
      <p className="error-banner__message">{message}</p>
      {action && (
        <button
          type="button"
          className="error-banner__action"
          onClick={action.onClick}
        >
          {action.label}
        </button>
      )}
      {onDismiss && (
        <button
          type="button"
          className="error-banner__dismiss"
          onClick={onDismiss}
          aria-label="Dismiss error"
        >
          ×
        </button>
      )}
    </div>
  );
}

export default ErrorBanner;
